import { useEffect, useState } from "react";
import type React from "react";
import {
  Button,
  FileDropZone,
  FileItem,
  Icon,
  useFileDropZone,
} from "@elaniintc/goes-design-system";
import { getMimeTypes } from "@/pages/digital-files/utils";
import type { DocumentUpload } from "@/hooks/use-document-uploads";
import { formatFileSize } from "@/utils/files";

/**
 * Un archivo de la lista, con el estado de su subida cuando el llamador la
 * administra. Sin `doc` se comporta como antes: nombre, peso y borrar.
 */
const UploadableFileItem = ({
  file,
  disabled,
  doc,
  retryDocument,
  oversizeError,
  onRemove,
}: {
  file: File;
  disabled: boolean;
  doc?: DocumentUpload;
  retryDocument?: (key: string) => void;
  oversizeError?: string;
  onRemove: () => void;
}) => (
  <div className="flex flex-col gap-1">
    <FileItem
      fileName={file.name}
      action={!disabled && <Icon type="far" name="trash" size="sm" />}
      actionAriaLabel="Borrar"
      tag={formatFileSize(file.size)}
      color={doc?.status === "error" ? "danger" : undefined}
      showProgress={doc?.status === "uploading"}
      progressValue={doc?.progress ?? 0}
      onAction={onRemove}
      error={
        oversizeError ?? (doc?.status === "error" ? doc.error : undefined)
      }
    />
    {/* El archivo sigue en memoria: reintentar no le pide nada al usuario más
        que el clic. */}
    {doc?.status === "error" && retryDocument && (
      <Button
        type="button"
        variant="quiet"
        size="xs"
        className="self-start cursor-pointer"
        onClick={() => retryDocument(doc.key)}
      >
        <Icon type="far" name="rotate-right" size="sm" />
        Reintentar
      </Button>
    )}
  </div>
);
interface FileInputProps {
  value: File[] | File | null;
  onChange: (val: File[] | File | null) => void;
  disabled?: boolean;
  acceptedMimeTypes?: string[];
  allowMultiples?: boolean;
  allowed_mimes?: string[];
  maxSizeBytes?: number | null;
  /**
   * Estado de subida por archivo. Opcional: quien no suba en el momento de
   * seleccionar —el nodo de n8n, por ejemplo— simplemente no lo pasa y la lista
   * se dibuja como antes, sin progreso ni reintento.
   */
  documents?: DocumentUpload[];
  retryDocument?: (key: string) => void;
}

export const FileInput = ({
  value,
  onChange,
  acceptedMimeTypes,
  disabled = false,
  allowMultiples = false,
  allowed_mimes = [],
  maxSizeBytes,
  documents,
  retryDocument,
}: FileInputProps) => {
  const [dragError, setDragError] = useState<string | null>(null);

  const hasFile = Array.isArray(value) ? value.length > 0 : Boolean(value);

  /** El estado de subida se casa con el archivo por identidad, no por nombre. */
  const documentFor = (file: File) =>
    documents?.find((doc) => doc.file === file);
  const mimeTypes = acceptedMimeTypes ?? getMimeTypes(allowed_mimes);

  const isMimeAllowed = (mime: string | null | undefined) => {
    if (allowed_mimes?.includes("*")) return true;
    if (!mime) return true;
    return mimeTypes.includes(mime);
  };

  const { setFiles, handleDrop, isDragging, setIsDragging, handleFileChange } =
    useFileDropZone({
      acceptedFileMimeTypes: mimeTypes.includes("*") ? ["*/*"] : mimeTypes,
      acceptedFileExtensions: allowed_mimes,
      maxFileSizeBytes: maxSizeBytes ?? 1024 * 1024 * 1024,
      cb: (files) => {
        setDragError(null);
        if (allowMultiples) {
          onChange(files);
        } else {
          onChange(files[0] || null);
        }
      },
    });

  useEffect(() => {
    if (value) {
      setFiles(Array.isArray(value) ? value : [value]);
    } else {
      setFiles([]);
    }
  }, [value, setFiles]);

  const handleContainerDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const dt = event.dataTransfer;
    if (!dt) return;

    let foundInvalid = false;
    let foundFile = false;

    if (dt.items && dt.items.length > 0) {
      Array.from(dt.items).forEach((item) => {
        if (item.kind === "file") {
          foundFile = true;
          const allowed = isMimeAllowed(item.type);
          if (!allowed) {
            foundInvalid = true;
          }
        }
      });
    } else if (dt.files && dt.files.length > 0) {
      Array.from(dt.files).forEach((file) => {
        foundFile = true;
        const allowed = isMimeAllowed(file.type);
        if (!allowed) {
          foundInvalid = true;
        }
      });
    }

    if (foundFile) {
      if (foundInvalid) {
        setDragError(
          "No puedes arrastrar este tipo de archivo. Verifica las extensiones permitidas.",
        );
      } else {
        setDragError(null);
      }
    }
  };

  const handleContainerDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleContainerDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <section
      className=" inset-0 pt-2 w-full"
      aria-label="Área de subida de archivos"
      onDragEnter={handleContainerDragEnter}
      onDragOver={handleContainerDragOver}
      onDragLeave={handleContainerDragLeave}
    >
      <div className="flex flex-col gap-4">
        {(allowMultiples || !hasFile) && !disabled && (
          <FileDropZone
            isDragging={isDragging}
            onFileDrop={handleDrop}
            setIsDragging={setIsDragging}
            onZoneClick={handleFileChange}
            acceptedFilesMimeTypes={mimeTypes}
            acceptedFilesExtensions={
              allowed_mimes?.includes("*")
                ? ["archivo de cualquier tipo"]
                : allowed_mimes || []
            }
          />
        )}

        <div className="flex flex-col gap-0.5">
          {Array.isArray(value)
            ? value.map((file: File) => (
                <UploadableFileItem
                  key={file.name}
                  file={file}
                  disabled={disabled}
                  doc={documentFor(file)}
                  retryDocument={retryDocument}
                  oversizeError={
                    maxSizeBytes && file.size > maxSizeBytes
                      ? "Archivo muy pesado, no se incluirá en el proceso."
                      : undefined
                  }
                  onRemove={() => {
                    const newFiles = value.filter(
                      (f: File) => f.name !== file.name,
                    );
                    onChange(newFiles);
                    setFiles(newFiles);
                  }}
                />
              ))
            : value && (
                <UploadableFileItem
                  key={value.name}
                  file={value}
                  disabled={disabled}
                  doc={documentFor(value)}
                  retryDocument={retryDocument}
                  oversizeError={
                    maxSizeBytes && value.size > maxSizeBytes
                      ? "Archivo muy pesado, no se incluirá en el proceso."
                      : undefined
                  }
                  onRemove={() => {
                    onChange(null);
                    setFiles([]);
                  }}
                />
              )}
        </div>
        {dragError && (
          <div className="text-danger flex items-center gap-1.5">
            <span className="text-current text-xs leading-xl font-normal">
              {dragError}
            </span>
          </div>
        )}
      </div>
    </section>
  );
};
